/**
 * How each shift opens.
 *
 * The problem this started as: first contact used to land around 360 s. Most shifts hung
 * their first hostile off a convoy hull's range — the right gate for an *ambush*, since it
 * is what puts the fight inside the gun's envelope — but the convoy spawns kilometres out,
 * so that gate could not possibly open early and it was gating the shift's entire opening
 * as a side effect. The fix was to give every shift a *different* first hostile: a small
 * probe that comes for the rig on a plain clock, owing nothing to where the convoy has got
 * to. The escort ambushes then land exactly where they always did.
 *
 * That worked, and then went too far. It shipped as a single shared constant, so eight of
 * twelve shifts opened with the same two skiffs, at the same second, from the same bearing,
 * at the same range — which is its own kind of boring, and the one a player notices
 * fastest because it is the first thing they see every single time.
 *
 * So it is a table. The mechanism is unchanged and the budget below is still enforced;
 * what varies is what comes, when, from where, and in how many pieces.
 *
 * Three shifts are deliberately absent. Shift 1 opens on its tutorial's single
 * first-blood skiff, shift 6 opens on a corvette that *is* the shift's first act rather
 * than a probe, and shift 8 opens on the last human enemies in the game. Those are
 * content, not openings, and forcing them into this shape would have described them
 * wrongly.
 *
 * Keys are the post-cut shift numbers (2026-07-30: the campaign went from twelve shifts
 * to eight — Long Shadows, Bank Fog, Consignment and All Of It were removed).
 */

import type { VesselKind } from '../../sim/types';
import type { SpawnBeat } from '../schema';

/** Adam's ceiling: more than this before the first hostile is a stretch. */
export const FIRST_CONTACT_BUDGET_SECONDS = 35;

export interface OpeningWave {
  vessel: VesselKind;
  count: number;
  rangeKm: number;
  bearingDeg: number;
  spreadDeg?: number;
  /**
   * Seconds after the opening's `spawnAt`. A wave may trickle in rather than arriving
   * whole — three hulls fifteen seconds apart on three bearings is a different problem
   * from three hulls abreast, and costs nothing to author.
   */
  delay?: number;
  /**
   * The probes were always described as coming for the rig — this is what makes it
   * true. Without it a probe spawned two kilometres out would ignore the platform and
   * chase a convoy hull six kilometres away across the whole sector, which is exactly
   * the "they only ever attack the convoy" Adam called out. Omitted only where the
   * hull's behaviour cannot honour it (the mini-sub) or the fiction wants the convoy.
   */
  mission?: 'rig';
}

export interface Opening {
  /** The warning. */
  radioAt: number;
  /** The hulls. Must leave `FIRST_CONTACT_BUDGET_SECONDS` intact. */
  spawnAt: number;
  waves: readonly OpeningWave[];
}

export const OPENINGS: Readonly<Record<number, Opening>> = {
  // Close in, because night ranges are set by the spotlight rather than by the gun: a
  // skiff is invisible past about 970 m after dark and the light reaches 1.4 km.
  2: {
    radioAt: 18,
    spawnAt: 22,
    waves: [{ vessel: 'skiff', count: 2, rangeKm: 1.25, bearingDeg: -52, spreadDeg: 12, mission: 'rig' }],
  },
  // Running in ahead of the weather — the storm is the shift's real opening.
  3: {
    radioAt: 18,
    spawnAt: 22,
    waves: [{ vessel: 'skiff', count: 2, rangeKm: 1.9, bearingDeg: -52, spreadDeg: 12, mission: 'rig' }],
  },
  // Deliberately ordinary. The shift's own idea arrives at 150 s and is worth nothing if
  // the player has spent the first two minutes bored rather than settled; something
  // familiar first is what makes the thing that follows strange.
  4: {
    radioAt: 16,
    spawnAt: 20,
    waves: [{ vessel: 'skiff', count: 2, rangeKm: 1.9, bearingDeg: -56, spreadDeg: 12, mission: 'rig' }],
  },
  /*
   * No warning and no surface pair: the shift opens on the thing itself.
   *
   * She spawns close, and has to. Every other opening is a surface hull the player can
   * see the moment it arrives; this one runs in submerged and undetectable, so the clock
   * between her spawn and her periscope is time with nothing on the radar and nothing to
   * shoot. From 2.0 km that ingress was 131 s and first contact landed at 2:18 — against
   * eight to twenty-two seconds for every shift either side. At 1.15 km she breaks the
   * surface around thirty seconds in, which is the same beat the neighbours open on, and
   * the shift still opens on the creature rather than on an escort of skiffs.
   */
  5: {
    radioAt: 15,
    spawnAt: 22,
    waves: [{ vessel: 'minisub', count: 1, rangeKm: 1.15, bearingDeg: -48 }],
  },
  // Plain hulls: the shift's dread is carried by the radio saying the sonar is blind,
  // not by what comes over the horizon first.
  7: {
    radioAt: 18,
    spawnAt: 22,
    waves: [{ vessel: 'skiff', count: 2, rangeKm: 1.9, bearingDeg: -54, spreadDeg: 12, mission: 'rig' }],
  },
};

/**
 * Endless generates its own opening from the ladder, but borrows the timing so a watch
 * starts as briskly as an authored shift does.
 */
export const ENDLESS_OPENING = {
  radioAt: 18,
  spawnAt: 22,
  rangeKm: 1.9,
  spreadDeg: 12,
} as const;

export function openingFor(index: number): Opening {
  const o = OPENINGS[index];
  if (!o) throw new Error(`shift ${index} has no opening in the table`);
  return o;
}

/**
 * The spawn beats for a shift's opening, tagged `probe`, `probe-b`, `probe-c`.
 *
 * Generated rather than hand-written so that adding a wave to an opening cannot
 * accidentally collide tags with a later beat, which `validateShift` would reject.
 */
export function openingBeats(index: number): SpawnBeat[] {
  const o = openingFor(index);
  return o.waves.map((w, i) => ({
    kind: 'spawn' as const,
    trigger: { at: o.spawnAt + (w.delay ?? 0) },
    tag: i === 0 ? 'probe' : `probe-${String.fromCharCode(97 + i)}`,
    vessel: w.vessel,
    count: w.count,
    rangeKm: w.rangeKm,
    bearingDeg: w.bearingDeg,
    ...(w.spreadDeg !== undefined ? { spreadDeg: w.spreadDeg } : {}),
    ...(w.mission !== undefined ? { mission: w.mission } : {}),
  }));
}
